import * as React from "react";
import isEqual from "lodash.isequal";
import axios, { AxiosResponse } from "axios";
import { RuleSetting, SavedRuleSetting } from "../types";
import { InsightType } from "../../../utils/routes";
import { IndexByNumber } from "../../../redux/modules/insights/utils";
import { useConfigDispatch } from ".";
import { ActiveRules } from "./types";

type SavedProjectRules = IndexByNumber<SavedRuleSetting[]>;

type ProjectConfigPayload = {
  id?: number;
  priority: number;
  projectKey: string;
  type: InsightType;
  rule: {
    id: number;
  };
};

export function useActions() {
  const dispatch = useConfigDispatch();

  const updateConfigRules = React.useCallback(function updateConfigRules(
    rule: RuleSetting
  ) {
    dispatch({ type: "UPDATE_CONFIG_RULES", rule });
  },
  []);

  const updateInsightType = React.useCallback(function updateInsightType(
    insightType: InsightType
  ) {
    dispatch({ type: "UPDATE_INSIGHT_TYPE", insightType });
  },
  []);

  const updateActiveProject = React.useCallback(function updateActiveProject(
    activeProject: string
  ) {
    dispatch({ type: "UPDATE_ACTIVE_PROJECT", activeProject });
  },
  []);

  const setSelectedRule = React.useCallback(function setSelectedRule(
    selectedRule: RuleSetting
  ) {
    dispatch({ type: "SET_SELECTED_RULE", selectedRule });
  },
  []);

  /**
   * Update single rule
   */
  const updateRuleConfig = React.useCallback(function updateRuleConfig(
    rule: RuleSetting
  ) {
    dispatch({ type: "UPDATE_RULE_CONFIG", rule });
  },
  []);

  /**
   * Update multiple rules at one
   */
  const bulkUpdateRuleConfig = React.useCallback(function bulkUpdateRuleConfig(
    rules: ActiveRules
  ) {
    dispatch({ type: "BULK_UPDATE_RULE_CONFIG", rules });
  },
  []);
  /**
   * Discard `active` state and reset `active`
   * to current `saved` state
   */
  const resetConfig = React.useCallback(function resetConfig() {
    dispatch({ type: "RESET_CONFIG" });
  }, []);

  const getProjectConfig = React.useCallback(async function getProjectConfig(
    projectKey: string,
    insightType: InsightType
  ) {
    dispatch({ type: "LOAD_CONFIG" });
    try {
      const request: AxiosResponse<SavedProjectRules> = await axios.post(
        `/v1/project-rules/search?type=${insightType}`,
        {
          projectKeys: [projectKey],
        }
      );

      if (request.status === 200) {
        const saved = Object.entries(request.data).reduce(
          (acc: any, [key, val]) => {
            acc[key] = {
              ...val[0],
              rule: {
                ...val[0].rule,
                id: Number(val[0].rule.id),
              },
            };
            return acc;
          },
          {}
        );
        dispatch({ type: "LOAD_CONFIG_SUCCESS", saved });
      } else {
        dispatch({ type: "LOAD_CONFIG_FAIL" });
      }
    } catch (error) {
      console.error(error);
    }
  },
  []);

  const saveProjectConfig = React.useCallback(async function saveProjectConfig(
    projectKey: string,
    saved: IndexByNumber<SavedRuleSetting>,
    active: IndexByNumber<RuleSetting | undefined>,
    insightType: InsightType
  ) {
    // * Sets the `isSaving` prop
    dispatch({ type: "SAVE_CONFIG" });

    // * If rule exists in `saved` but doesn't in `active`
    // * add the `id` from saved to array of rules to disable
    const disable = Object.entries(saved).reduce(
      (del: number[], [key, val]) => {
        if (active[Number(key)] === undefined) {
          del.push(val.id);
        }
        return del;
      },
      []
    );

    // * If rule doesn't match a saved rule (i.e., undefined or different properties)
    // * add rule to config payload
    const enable = Object.values(active).reduce(
      (acc: ProjectConfigPayload[], rule) => {
        if (rule && !isEqual(saved[rule.rule.id], rule)) {
          acc.push({
            ...rule,
            projectKey: projectKey,
            type: insightType,
          });
          return acc;
        }

        return acc;
      },
      []
    );

    try {
      const response: AxiosResponse<{
        enabled: SavedProjectRules;
        disabled: number[];
      }> = await axios.post("/v1/project-rules/update", {
        enable,
        disable,
      });

      if (response.status === 200) {
        // * Fetch the latest up to date saved config
        void getProjectConfig(projectKey, insightType);
      }
    } catch (error) {
      console.error(error);
      dispatch({ type: "LOAD_CONFIG_FAIL" });
    }
  },
  []);

  return {
    updateConfigRules,
    updateInsightType,
    updateActiveProject,
    setSelectedRule,
    updateRuleConfig,
    bulkUpdateRuleConfig,
    resetConfig,
    getProjectConfig,
    saveProjectConfig,
  };
}
